<script lang="ts" setup>
import { debounce, ref, watch } from '#imports';

type Props = {
    isLoading?: boolean;
    error?: string;
    closeHandler?: () => void;
    containerClass?: string;
    withoutRemoveIcon?: boolean;
    initialValue?: string;
    isFocused?: boolean;
};

const props = defineProps<Props>();

const searchQuery = ref(props?.initialValue || '');

const inputRef = ref<Nullable<HTMLInputElement>>(null);

const emit = defineEmits<{
    (e: 'onChangeDebounce', value: string): void;
    (e: 'onFocus', value: Nullable<FocusEvent>): void;
}>();

watch(
    [searchQuery],
    debounce(
        () => emit(
            'onChangeDebounce',
            searchQuery.value
        ),
        500
    )
);

watch(
    () => props.isFocused,
    (newVal) => {
        if (newVal && inputRef.value) {
            inputRef.value.focus();
        }
    }
);

</script>

<template>
  <div class="container-input"
       :class="containerClass"
  >
    <Transition name="fade"
                mode="out-in"
    >
      <LoaderSpinner v-if="isLoading"
                     key="loading"
                     class="input-svg"
                     :size="24"
      />
      <div v-else
           class="input-svg"
      >
        <IconMain key="search"
                  icon="search"
                  :size="24"
        />
      </div>
    </Transition>

    <input ref="inputRef"
           v-model="searchQuery"
           type="text"
           placeholder="Search"
           class="input-main input-search compact-500-default"
           :class="error && ''"
           v-bind="$attrs"
           @focus="(val) => emit('onFocus', val)"
    >
    <span v-if="error"
          class="input-search__error input-search__icon--right"
    >{{ error }}</span>
    <div v-if="!error && searchQuery.length && !withoutRemoveIcon"
         class="input-search__icon--right cursor-pointer"
         type="buton"
         @click="searchQuery = ''"
    >
      <IconMain :size="24"
                icon="close"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>

.container-input {
  position: relative;
}

.input-svg {
  position: absolute;
  top: 14px;
  left: 18px;
}

.input-search {
  display: flex;
  align-items: center;
  align-self: stretch;
  min-width: var(--control-500-size-single, 52px);
  height: var(--control-500-size-single, 52px);
  padding: 0 12px 0 48px;
  border: 2px solid transparent;
  border-radius: var(--control-500-radius, 2px);
  background: var(--bg-secondary);
  gap: var(--label-icon-500-gap, 8px);

  &__icon--right {
    position: absolute;
    top: 14px;
    right: 18px;
  }

  &__error {
    color: var(--content-secondary)
  }

  &:focus {
    border: var(--control-focus-ring-width, 2px) solid var(--border-brand-aviation);
    background: var(--content-secondary-inverted);
  }
}
</style>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
